import { BatchedMeshTarget } from 'app/modules/wallforming/components/scenes/objects/articles/types/batchedMeshTarget';
import {
    addInstancesToBatchedMesh,
    isBatchedMeshSetActive,
    registerGeometryInBatchedMesh,
    removeInstancesFromBatchedMesh
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/batchedMeshUtils';

import type { ArticleGeometrySlots } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleGeometrySlots';
import type { BatchedMeshMaps } from 'app/modules/wallforming/components/scenes/objects/articles/types/batchedMeshMaps';
import type { InstanceBatchIds } from 'app/modules/wallforming/components/scenes/objects/articles/types/instanceBatchIds';
import type { ArticleBatchSliceState } from 'app/modules/wallforming/components/state-management/placement/states/articleBatchSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

export const createArticleBatchSlice: StateCreator<PlacementState, [], [], ArticleBatchSliceState> = (set, get) => ({
    getBatchedMeshes: (isMainScene) => (isMainScene ? get().batchedMeshes : get().navigatorBatchedMeshes),

    // ── Main canvas ────────────────────────────────────────────────────────────
    batchedMeshes: {
        physical: null,
        sheet: null,
        holes: null
    },
    setBatchedMeshes: (physical, sheet, holes) => {
        set({ batchedMeshes: { physical, sheet, holes } });
    },

    articleGeometrySlots: new Map<string, ArticleGeometrySlots>(),
    instanceBatchIds: new Map<string, InstanceBatchIds>(),

    physicalBatchToInstance: new Map<number, string>(),
    sheetBatchToInstance: new Map<number, string>(),
    holesBatchToInstance: new Map<number, string>(),

    isBatchModeActive: () => {
        return isBatchedMeshSetActive(get().batchedMeshes);
    },

    registerArticleGeometry: (articleId, meshes) => {
        const state = get();
        const ctx: BatchedMeshMaps = {
            meshes: state.batchedMeshes,
            geometrySlots: state.articleGeometrySlots,
            instanceBatchIds: state.instanceBatchIds,
            physicalBatchToInstance: state.physicalBatchToInstance,
            sheetBatchToInstance: state.sheetBatchToInstance,
            holesBatchToInstance: state.holesBatchToInstance
        };
        registerGeometryInBatchedMesh(ctx, articleId, meshes, BatchedMeshTarget.MainScene);
    },

    addBatchInstances: (articleId, articleInstanceIds) => {
        const state = get();
        const ctx: BatchedMeshMaps = {
            meshes: state.batchedMeshes,
            geometrySlots: state.articleGeometrySlots,
            instanceBatchIds: state.instanceBatchIds,
            physicalBatchToInstance: state.physicalBatchToInstance,
            sheetBatchToInstance: state.sheetBatchToInstance,
            holesBatchToInstance: state.holesBatchToInstance
        };
        addInstancesToBatchedMesh(ctx, articleId, articleInstanceIds, BatchedMeshTarget.MainScene);
    },

    removeBatchInstances: (articleInstanceIds) => {
        const state = get();
        const ctx: BatchedMeshMaps = {
            meshes: state.batchedMeshes,
            geometrySlots: state.articleGeometrySlots,
            instanceBatchIds: state.instanceBatchIds,
            physicalBatchToInstance: state.physicalBatchToInstance,
            sheetBatchToInstance: state.sheetBatchToInstance,
            holesBatchToInstance: state.holesBatchToInstance
        };
        removeInstancesFromBatchedMesh(ctx, articleInstanceIds, BatchedMeshTarget.MainScene);
    },

    // ── Navigation canvas ───────────────────────────────────────────────────────
    navigatorBatchedMeshes: {
        physical: null,
        sheet: null,
        holes: null
    },
    setNavigatorBatchedMeshes: (physical, sheet, holes) => {
        set({ navigatorBatchedMeshes: { physical, sheet, holes } });
    },

    navigatorGeometrySlots: new Map<string, ArticleGeometrySlots>(),
    navigatorInstanceBatchIds: new Map<string, InstanceBatchIds>(),

    isNavigatorBatchModeActive: () => {
        return isBatchedMeshSetActive(get().navigatorBatchedMeshes);
    },

    registerNavigatorArticleGeometry: (articleId, meshes) => {
        const state = get();
        const ctx: BatchedMeshMaps = {
            meshes: state.navigatorBatchedMeshes,
            geometrySlots: state.navigatorGeometrySlots,
            instanceBatchIds: state.navigatorInstanceBatchIds
        };
        registerGeometryInBatchedMesh(ctx, articleId, meshes, BatchedMeshTarget.NavigatorScene);
    },

    addNavigatorBatchInstances: (articleId, articleInstanceIds) => {
        const state = get();
        const ctx: BatchedMeshMaps = {
            meshes: state.navigatorBatchedMeshes,
            geometrySlots: state.navigatorGeometrySlots,
            instanceBatchIds: state.navigatorInstanceBatchIds
        };
        addInstancesToBatchedMesh(ctx, articleId, articleInstanceIds, BatchedMeshTarget.NavigatorScene);
    },

    removeNavigatorBatchInstances: (articleInstanceIds) => {
        const state = get();
        const ctx: BatchedMeshMaps = {
            meshes: state.navigatorBatchedMeshes,
            geometrySlots: state.navigatorGeometrySlots,
            instanceBatchIds: state.navigatorInstanceBatchIds
        };
        removeInstancesFromBatchedMesh(ctx, articleInstanceIds, BatchedMeshTarget.NavigatorScene);
    }
});
